// BIG5/BLOK 2 — Verlofprofiel-tab herberekenings-engine.
//
// Pure rekenfuncties (geen DB-imports) voor het wijzigen van het
// MedewerkerVerlofprofiel. Twee hoofd-functies:
//
//   - berekenDagUren / berekenAtvUrenPerWeek — kleine helpers, zowel
//     server- als clientside bruikbaar, voor de readonly-outputs op de
//     verlofprofiel-tab (daguren, ATV-uren per week).
//   - berekenProfielWijzigingImpact — vergelijkt huidig profiel met
//     gewenst nieuw profiel + huidige LeaveEntitlement-saldi en geeft
//     per-emmer impact + business-rule-fouten + waarschuwingen terug.
//
// Business rules BR-1 t/m BR-6 zoals beschreven in BLOK 2-brief:
//   BR-1 contracturen-wijziging → pro-rata herberekening van saldi
//   BR-2 ATV aan/uit → alleen geldig bij contractUren===38 && werkUren===40
//   BR-3 startDate-wijziging → guards op opgenomen verlof vóór nieuwe start
//   BR-4 endDate-wijziging   → guards op aanvragen na nieuwe einddatum
//   BR-5 werkUren-wijziging  → ATV auto-disable wanneer condities niet meer
//                              kloppen
//   BR-6 TVT-toggles uit     → bevries (geen verdere opbouw, wel zichtbaar)
//
// Geen DB-schrijven hier; de API-route doet de Prisma-transactie en
// gebruikt dit resultaat als blueprint.
//
// Eenheden: alle uren in kwartier-precisie (regel 1, leave-calculations).

import { berekenDaguren, calculateWerkdagen, type PublicHolidayInfo } from './leave-calculations'
import { SPAAR_KEUZE_LABELS, WERKTIJD_SPAREN_LABELS } from '@streep/types'

// ────────────────────────────────────────────────────────────────────────────
// Types — input/output (matchen `@streep/types/verlofprofiel`)
// ────────────────────────────────────────────────────────────────────────────

// Batch-6: + ouderschap/senioren-emmers (zelfde unie als SaldoEmmerType in
// leave-calculations; opbouw-/profielberekeningen raken alleen de eerste 5).
export type EmmerType =
  | 'WETTELIJK'
  | 'BOVENWETTELIJK'
  | 'ATV'
  | 'TVT_WERK'
  | 'TVT_REIS'
  | 'OUDERSCHAP_BETAALD'
  | 'OUDERSCHAP_ONBETAALD'
  | 'SENIORENVERLOF'

export type FoutCode =
  | 'ATV_VEREIST_38u_40u'
  | 'STARTDATUM_NA_OPGENOMEN_VERLOF'
  | 'EINDDATUM_VOOR_OPENSTAANDE_AANVRAAG'
  | 'INVALID_PROFILE'

export interface VerlofProfielSnapshot {
  contractHoursPerWeek: number
  werkUrenPerWeek: number | null
  werkdagenPerWeek?: number // werkdagen per week (default 5); deler voor daguren
  atvHoursPerWeek: number
  atvEnabled: boolean
  tvtWerkurenEnabled: boolean
  tvtReisurenEnabled: boolean
  // FO-122 — opbouwpercentage per TVT-soort (50 of 100). Optioneel: raakt de
  // saldo-impact niet (TVT bouwt via werkbon-mutaties op, niet pro-rata), maar
  // hoort wel bij het volledige profiel. Afwezig => 100 (volledige opbouw).
  tvtWerkOpbouwPercentage?: number
  tvtReisOpbouwPercentage?: number
  teamleiderSaldoInzicht: boolean
  caoBeleidId: string | null
  startDate: Date | null
  endDate: Date | null
  verlofBijzonderheden: string | null
}

export interface EntitlementSnapshot {
  type: EmmerType
  year: number
  hours: number
  usedHours: number
}

export interface AanvraagSnapshot {
  id: string
  startDate: Date
  endDate: Date | null
  status: string
}

export interface BeleidSnapshot {
  wettelijkFactor: number // default 4
  bovenwettelijkFactor: number // default 1
  atvUrenPerJaar: number // default 104
  atvAlleenBij38u40u: boolean // default true
}

export interface ProfielWijzigingFout {
  code: FoutCode
  veld?: keyof VerlofProfielSnapshot
  message: string
}

export interface SaldoImpactRegel {
  emmerType: EmmerType
  jaar: number
  oudeToekenning: number
  nieuweToekenning: number
  delta: number
  toelichting: string
}

export interface ProblematischeAanvraag {
  aanvraagId: string
  startDate: Date
  endDate: Date | null
  status: string
  reden: 'NA_EINDDATUM' | 'VOOR_STARTDATUM'
}

export interface BerekenInput {
  /** Huidig profiel (vóór wijziging). */
  oudProfiel: VerlofProfielSnapshot
  /** Voorgesteld nieuw profiel. Gehele snapshot, niet partial. */
  nieuwProfiel: VerlofProfielSnapshot
  /** Huidige entitlements (LeaveEntitlement-rows) voor het peiljaar. */
  entitlements: EntitlementSnapshot[]
  /** Verlofaanvragen die niet INGETROKKEN zijn — voor start/eind-guards. */
  aanvragen: AanvraagSnapshot[]
  /** Actief CAO-beleid. */
  beleid: BeleidSnapshot
  /** Peiljaar (default = huidig jaar). */
  jaar: number
  /** Feestdagen voor pro-rata werkdagen-berekening. */
  feestdagen: PublicHolidayInfo[]
  /**
   * batch-58 uitdienst-verlof (FO-424 §7.1) — het aanroepende pad corrigeert
   * verlof ná de nieuwe einddatum ZELF (intrekken/inkorten/afwijzen +
   * terugboeking). BR-4 wordt dan een WAARSCHUWING i.p.v. een blokkade: de
   * uitdiensttreding hoefde daardoor niet meer stuk te lopen op verlof dat het
   * systeem een seconde later toch al opruimt.
   *
   * Alleen zetten wanneer de caller die correctie daadwerkelijk uitvoert (de
   * employees-PATCH doet dat). Paden die de correctie NIET draaien - zoals de
   * verlofprofiel-PATCH - laten dit uit en houden de harde blokkade, want daar
   * zou het verlof anders stil blijven staan.
   */
  einddatumAanvragenAutomatisch?: boolean
}

export interface ProfielWijzigingImpactResult {
  saldoImpact: SaldoImpactRegel[]
  fouten: ProfielWijzigingFout[]
  waarschuwingen: string[]
  problematischeAanvragen: ProblematischeAanvraag[]
  /** Auto-aangepast profiel (bv. atvEnabled=false bij BR-5). */
  effectiefNieuwProfiel: VerlofProfielSnapshot
  veiligOmDoorTeVoeren: boolean
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers — daguren + ATV-uren-per-week (BR-2, BR-5)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Daguren — werkdag-norm voor verlof-aantallen.
 *
 * Dagkost = werkelijke werkuren per week / werkdagen per week (zie
 * `berekenDaguren` in leave-calculations — één formule). ATV zit in de teller:
 * bij een geldige 38u->40u-combo zijn de werkuren 40, anders de contracturen
 * (of een expliciet gezet `werkUren`). `werkdagenPerWeek` (default 5) is de
 * deler, zodat 4-daagse contracten (36/4=9) ook kloppen.
 *
 * Publiek gemaakt voor (a) de tab-UI en (b) impact-berekening hieronder.
 */
/**
 * Effectieve werkuren per week - de ENE bron voor de ATV-combo-check. Een
 * expliciet gezet `werkUren` wint; anders 40 bij een geldige 38u->40u ATV-combo
 * (`atvEnabled`), anders de contracturen.
 *
 * Cruciaal: `werkUren` blijft bij ATV-medewerkers in de praktijk NULL (de norm).
 * Door deze afleiding overal te gebruiken lopen dagkost (`berekenDagUren`) en
 * ATV-opbouw (`berekenAtvUrenPerWeek` / `calculateEntitlementHours`) NOOIT meer
 * uiteen: zonder dit leverde de opbouw 0 ATV terwijl de dagkost al 40u/8u rekende.
 *
 * De ATV-combo is per CAO M&T altijd 38u→40u, dus hardcoden we 40 (niet
 * `contractUren + atvHoursPerWeek`). Dat komt overeen met `getTotaalWerkbareUren`
 * (API-kant) zolang `atvHoursPerWeek` = 2 (de CAO-standaard; prod-breed het geval),
 * en de ATV-guard toetst sowieso strikt op `=== 40`.
 */
export function effectieveWerkUren(
  contractUren: number,
  werkUren: number | null,
  atvEnabled: boolean,
): number {
  return werkUren ?? (atvEnabled && contractUren === 38 ? 40 : contractUren)
}

export function berekenDagUren(
  contractUren: number,
  werkUren: number | null,
  atvEnabled: boolean,
  werkdagenPerWeek = 5,
): number {
  if (contractUren <= 0) return 0
  return berekenDaguren(effectieveWerkUren(contractUren, werkUren, atvEnabled), werkdagenPerWeek)
}

/**
 * ATV-uren per week voor de medewerker. Alleen positief bij geldige
 * 38u→40u-combo + atvEnabled. Anders 0. Gebruikt `effectieveWerkUren` zodat een
 * niet-gezette `werkUren` bij een ATV-38u-medewerker correct als 40 telt.
 */
export function berekenAtvUrenPerWeek(
  contractUren: number,
  werkUren: number | null,
  atvEnabled: boolean,
): number {
  if (!atvEnabled) return 0
  const wu = effectieveWerkUren(contractUren, werkUren, atvEnabled)
  if (contractUren !== 38 || wu !== 40) return 0
  return Math.max(0, wu - contractUren)
}

// ────────────────────────────────────────────────────────────────────────────
// Pro-rata-helpers (BR-1, BR-3, BR-4)
// ────────────────────────────────────────────────────────────────────────────

function clampToYear(jaar: number, datum: Date | null, side: 'start' | 'eind'): Date {
  const yearStart = new Date(jaar, 0, 1)
  const yearEnd = new Date(jaar, 11, 31)
  if (!datum) return side === 'start' ? yearStart : yearEnd
  if (side === 'start') return datum < yearStart ? yearStart : datum
  return datum > yearEnd ? yearEnd : datum
}

function proRataFactor(
  jaar: number,
  startDate: Date | null,
  endDate: Date | null,
  feestdagen: PublicHolidayInfo[],
): number {
  const yearStart = new Date(jaar, 0, 1)
  const yearEnd = new Date(jaar, 11, 31)
  const periodeStart = clampToYear(jaar, startDate, 'start')
  const periodeEind = clampToYear(jaar, endDate, 'eind')
  const werkdagenInJaar = calculateWerkdagen(yearStart, yearEnd, feestdagen)
  if (werkdagenInJaar === 0) return 0
  const werkdagenInPeriode = calculateWerkdagen(periodeStart, periodeEind, feestdagen)
  return werkdagenInPeriode / werkdagenInJaar
}

function roundTwo(uren: number): number {
  return Math.round(uren * 100) / 100
}

// ────────────────────────────────────────────────────────────────────────────
// BR-2: ATV-toggle valideren
// ────────────────────────────────────────────────────────────────────────────

/**
 * ATV mag ALLEEN aan staan bij 38u contract + 40u werken (CAO M&T).
 * Bij andere combinaties: harde fout (geen auto-correct hier — caller
 * moet beslissen of dit een fout of een auto-disable wordt).
 */
function valideerAtvCombi(profiel: VerlofProfielSnapshot): ProfielWijzigingFout | null {
  if (!profiel.atvEnabled) return null
  const wu = effectieveWerkUren(profiel.contractHoursPerWeek, profiel.werkUrenPerWeek ?? null, profiel.atvEnabled)
  if (profiel.contractHoursPerWeek === 38 && wu === 40) return null
  return {
    code: 'ATV_VEREIST_38u_40u',
    veld: 'atvEnabled',
    message:
      'ATV kan alleen aan staan bij 38u contract én 40u werken (CAO Metaal en Techniek).',
  }
}

// ────────────────────────────────────────────────────────────────────────────
// BR-1: nieuwe toekenning per emmer berekenen op basis van nieuw profiel
// ────────────────────────────────────────────────────────────────────────────

function berekenNieuweToekenning(
  emmerType: EmmerType,
  profiel: VerlofProfielSnapshot,
  beleid: BeleidSnapshot,
  jaar: number,
  feestdagen: PublicHolidayInfo[],
): number {
  const factor = proRataFactor(jaar, profiel.startDate, profiel.endDate, feestdagen)
  const cu = profiel.contractHoursPerWeek
  const wu = effectieveWerkUren(cu, profiel.werkUrenPerWeek ?? null, profiel.atvEnabled)

  if (emmerType === 'WETTELIJK') {
    return roundTwo(beleid.wettelijkFactor * cu * factor)
  }
  if (emmerType === 'BOVENWETTELIJK') {
    return roundTwo(beleid.bovenwettelijkFactor * cu * factor)
  }
  if (emmerType === 'ATV') {
    if (!profiel.atvEnabled) return 0
    if (beleid.atvAlleenBij38u40u && (cu !== 38 || wu !== 40)) return 0
    return roundTwo(beleid.atvUrenPerJaar * factor)
  }
  // TVT_WERK / TVT_REIS: bouwen op via mutaties (werkbon-uren), niet via
  // pro-rata-formule. Bij toggle-uit "bevriezen" we (BR-6): toekenning
  // blijft staan zoals die was; we geven hier 'undefined' terug door 0
  // te returnen, en de impact-functie behandelt TVT apart.
  return 0
}

// ────────────────────────────────────────────────────────────────────────────
// Hoofd-functie: berekenProfielWijzigingImpact
// ────────────────────────────────────────────────────────────────────────────

export function berekenProfielWijzigingImpact(
  input: BerekenInput,
): ProfielWijzigingImpactResult {
  const fouten: ProfielWijzigingFout[] = []
  const waarschuwingen: string[] = []
  const problematischeAanvragen: ProblematischeAanvraag[] = []
  const saldoImpact: SaldoImpactRegel[] = []

  // Werk met een mutable kopie zodat BR-5 (auto-disable ATV) door kan
  // werken zonder de caller-input aan te tasten.
  const effectief: VerlofProfielSnapshot = { ...input.nieuwProfiel }

  // ── BR-5: werkUrenPerWeek wijzigt + ATV blijft aan, maar combi is niet
  //   meer 38u→40u → ATV automatisch uit + waarschuwing.
  {
    const wu = effectieveWerkUren(
      effectief.contractHoursPerWeek,
      effectief.werkUrenPerWeek ?? null,
      effectief.atvEnabled,
    )
    if (
      effectief.atvEnabled &&
      input.beleid.atvAlleenBij38u40u &&
      (effectief.contractHoursPerWeek !== 38 || wu !== 40)
    ) {
      effectief.atvEnabled = false
      waarschuwingen.push(
        'ATV is automatisch uitgezet: contract is niet meer 38u + 40u werken (BR-5).',
      )
    }
  }

  // ── BR-2: na BR-5 nog een keer valideren — als ATV óók in input al
  //   ingeschakeld stond zonder geldige combi, melden als harde fout.
  {
    const f = valideerAtvCombi(effectief)
    if (f) fouten.push(f)
  }

  // ── BR-3: startDate-wijziging — opgenomen verlof vóór nieuwe start
  //   blokkeert (zou negatief saldo / inconsistentie geven).
  if (effectief.startDate && (!input.oudProfiel.startDate ||
      input.oudProfiel.startDate.getTime() !== effectief.startDate.getTime())) {
    const probleem = input.aanvragen.filter(
      (a) =>
        a.startDate < effectief.startDate! &&
        a.status !== 'INGETROKKEN' &&
        // batch-58 (FO-424): de HR-afkeurstatus heet in `LeaveStatus` gewoon
        // AFGEKEURD; 'AFGEKEURD_HR' bestaat daar niet en filterde dus nooit iets weg.
        // Sinds de uitdienst-stap systematisch AFGEKEURD-aanvragen ná de einddatum
        // produceert, blokkeerde dat de verlofprofiel-PATCH permanent op verlof dat
        // allang is afgewezen.
        a.status !== 'AFGEKEURD' &&
        a.status !== 'AFGEKEURD_TEAMLEIDER' &&
        a.status !== 'CONCEPT',
    )
    for (const a of probleem) {
      problematischeAanvragen.push({
        aanvraagId: a.id,
        startDate: a.startDate,
        endDate: a.endDate,
        status: a.status,
        reden: 'VOOR_STARTDATUM',
      })
    }
    if (probleem.length > 0) {
      fouten.push({
        code: 'STARTDATUM_NA_OPGENOMEN_VERLOF',
        veld: 'startDate',
        message: `${probleem.length} aanvraag/aanvragen liggen vóór de nieuwe startdatum - wijzig deze eerst.`,
      })
    }
  }

  // ── BR-4: endDate-wijziging — aanvragen ná nieuwe einddatum waarschuwen
  if (effectief.endDate && (!input.oudProfiel.endDate ||
      input.oudProfiel.endDate.getTime() !== effectief.endDate.getTime())) {
    const probleem = input.aanvragen.filter(
      (a) =>
        a.startDate > effectief.endDate! &&
        a.status !== 'INGETROKKEN' &&
        // batch-58 (FO-424): de HR-afkeurstatus heet in `LeaveStatus` gewoon
        // AFGEKEURD; 'AFGEKEURD_HR' bestaat daar niet en filterde dus nooit iets weg.
        // Sinds de uitdienst-stap systematisch AFGEKEURD-aanvragen ná de einddatum
        // produceert, blokkeerde dat de verlofprofiel-PATCH permanent op verlof dat
        // allang is afgewezen.
        a.status !== 'AFGEKEURD' &&
        a.status !== 'AFGEKEURD_TEAMLEIDER' &&
        // Een niet-ingediend klad wordt door de uitdienst-stap bewust niet
        // afgewezen; hem hier meetellen belooft iets dat niet gebeurt.
        a.status !== 'CONCEPT',
    )
    for (const a of probleem) {
      problematischeAanvragen.push({
        aanvraagId: a.id,
        startDate: a.startDate,
        endDate: a.endDate,
        status: a.status,
        reden: 'NA_EINDDATUM',
      })
    }
    if (probleem.length > 0) {
      if (input.einddatumAanvragenAutomatisch) {
        // FO-424: het aanroepende pad trekt deze aanvragen zelf in of kort ze in
        // (met terugboeking + audit + melding). Blokkeren zou de uitdiensttreding
        // tegenhouden voor iets dat direct daarna automatisch is opgelost.
        waarschuwingen.push(
          `${probleem.length} verlofaanvraag/-aanvragen liggen ná de nieuwe einddatum en worden automatisch ingetrokken, ingekort of afgewezen.`,
        )
      } else {
        fouten.push({
          code: 'EINDDATUM_VOOR_OPENSTAANDE_AANVRAAG',
          veld: 'endDate',
          message: `${probleem.length} aanvraag/aanvragen liggen ná de nieuwe einddatum - wijzig of trek deze eerst in.`,
        })
      }
    }
  }

  // ── BR-1: per-emmer impact — delta = toekenning(nieuw profiel) −
  //   toekenning(oud profiel). BELANGRIJK: de baseline is de toekenning ONDER
  //   HET OUDE PROFIEL, niet de opgeslagen `LeaveEntitlement.hours`. Zo
  //   reflecteert de delta uitsluitend wat dit profiel-edit verandert (de
  //   start-/einddatum-pro-rata), en NIET het verschil tussen de jaartoekenning
  //   en een naïeve heel-jaar-herberekening. Contracturen staan op deze tab
  //   read-only en lopen via de Contract-tab (append-only CORRECTIE); ze zijn
  //   dus gelijk in oud en nieuw en dragen 0 delta bij → geen dubbeltelling en
  //   geen saldo-corruptie bij een onschuldige her-opslag.
  const nonTvtTypes: EmmerType[] = ['WETTELIJK', 'BOVENWETTELIJK', 'ATV']
  for (const type of nonTvtTypes) {
    const oud = berekenNieuweToekenning(type, input.oudProfiel, input.beleid, input.jaar, input.feestdagen)
    const nieuw = berekenNieuweToekenning(type, effectief, input.beleid, input.jaar, input.feestdagen)
    const delta = roundTwo(nieuw - oud)
    if (Math.abs(delta) >= 0.01) {
      saldoImpact.push({
        emmerType: type,
        jaar: input.jaar,
        oudeToekenning: roundTwo(oud),
        nieuweToekenning: roundTwo(nieuw),
        delta,
        toelichting: profielChangeToelichting(type, input.oudProfiel, effectief),
      })
    }
  }

  // ── BR-6: TVT-toggles van aan→uit → bevriezen, geen impact-row maar
  //   wel waarschuwing. Van uit→aan: óók waarschuwing (gebruiker moet
  //   weten dat TVT pas opbouwt vanaf nu).
  // batch-68 (FO-344 §3.4) - in de woorden van de woordenlijst.
  const OVERUREN = WERKTIJD_SPAREN_LABELS.overuren.label
  const REISTIJD = WERKTIJD_SPAREN_LABELS.reistijd.label
  if (input.oudProfiel.tvtWerkurenEnabled && !effectief.tvtWerkurenEnabled) {
    waarschuwingen.push(`${OVERUREN} is uitgezet: de gespaarde overuren blijven staan, maar er komt niets meer bij (BR-6).`)
  }
  // Wat er met een uur gebeurt bij een percentage (50 = half, anders alles).
  const deelOveruren = (pct: number) =>
    pct === 50
      ? 'gaat de helft van het week-overwerk naar de spaarpot; de andere helft wordt uitbetaald'
      : 'gaat al het week-overwerk naar de spaarpot; alleen de toeslag wordt uitbetaald'
  const deelReistijd = (pct: number) =>
    pct === 50
      ? 'gaat de helft van de uitbetaalbare reistijd naar de spaarpot; de andere helft wordt uitbetaald'
      : 'gaat alle uitbetaalbare reistijd naar de spaarpot'
  // Zonder ATV is de opbouwfactor 0 (`tvtFactor`): dan belooft de tekst geen sparen.
  const zonderAtv = (label: string, wat: string) =>
    `${label} staat aan, maar zonder ATV heeft dat geen effect: ${wat} wordt uitbetaald.`
  if (!input.oudProfiel.tvtWerkurenEnabled && effectief.tvtWerkurenEnabled) {
    waarschuwingen.push(
      effectief.atvEnabled
        ? `${OVERUREN} is aangezet: vanaf nu ${deelOveruren(effectief.tvtWerkOpbouwPercentage ?? 100)}.`
        : zonderAtv(OVERUREN, 'het week-overwerk'),
    )
  }
  if (input.oudProfiel.tvtReisurenEnabled && !effectief.tvtReisurenEnabled) {
    waarschuwingen.push(`${REISTIJD} is uitgezet: de gespaarde reistijd blijft staan, maar er komt niets meer bij (BR-6).`)
  }
  if (!input.oudProfiel.tvtReisurenEnabled && effectief.tvtReisurenEnabled) {
    waarschuwingen.push(
      effectief.atvEnabled
        ? `${REISTIJD} is aangezet: vanaf nu ${deelReistijd(effectief.tvtReisOpbouwPercentage ?? 100)}.`
        : zonderAtv(REISTIJD, 'de uitbetaalbare reistijd'),
    )
  }

  // FO-122 — opbouwpercentage (50/100) gewijzigd, alleen relevant zolang de
  // bijbehorende TVT aanstaat. Informatief; raakt de saldo-impact niet (TVT
  // bouwt incrementeel via werkbon-mutaties, niet via deze pro-rata-engine).
  const oudWerkPct = input.oudProfiel.tvtWerkOpbouwPercentage ?? 100
  const nieuwWerkPct = effectief.tvtWerkOpbouwPercentage ?? 100
  const keuzeLabel = (pct: number) => SPAAR_KEUZE_LABELS[pct === 50 ? 'HALF' : 'ALLES']
  if (effectief.atvEnabled && effectief.tvtWerkurenEnabled && oudWerkPct !== nieuwWerkPct) {
    waarschuwingen.push(
      `${OVERUREN} gewijzigd van "${keuzeLabel(oudWerkPct)}" naar "${keuzeLabel(nieuwWerkPct)}": vanaf nu ${deelOveruren(nieuwWerkPct)}.`,
    )
  }
  const oudReisPct = input.oudProfiel.tvtReisOpbouwPercentage ?? 100
  const nieuwReisPct = effectief.tvtReisOpbouwPercentage ?? 100
  if (effectief.atvEnabled && effectief.tvtReisurenEnabled && oudReisPct !== nieuwReisPct) {
    waarschuwingen.push(
      `${REISTIJD} gewijzigd van "${keuzeLabel(oudReisPct)}" naar "${keuzeLabel(nieuwReisPct)}": vanaf nu ${deelReistijd(nieuwReisPct)}.`,
    )
  }

  return {
    saldoImpact,
    fouten,
    waarschuwingen,
    problematischeAanvragen,
    effectiefNieuwProfiel: effectief,
    veiligOmDoorTeVoeren: fouten.length === 0,
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Beschrijvingen voor audit-toelichtingen
// ────────────────────────────────────────────────────────────────────────────

function profielChangeToelichting(
  type: EmmerType,
  oud: VerlofProfielSnapshot,
  nieuw: VerlofProfielSnapshot,
): string {
  const parts: string[] = []
  if (oud.contractHoursPerWeek !== nieuw.contractHoursPerWeek) {
    parts.push(`contracturen ${oud.contractHoursPerWeek}→${nieuw.contractHoursPerWeek}`)
  }
  if ((oud.werkUrenPerWeek ?? oud.contractHoursPerWeek) !== (nieuw.werkUrenPerWeek ?? nieuw.contractHoursPerWeek)) {
    parts.push(
      `werkuren ${oud.werkUrenPerWeek ?? oud.contractHoursPerWeek}→${nieuw.werkUrenPerWeek ?? nieuw.contractHoursPerWeek}`,
    )
  }
  if (oud.atvEnabled !== nieuw.atvEnabled) {
    parts.push(`ATV ${oud.atvEnabled ? 'aan' : 'uit'}→${nieuw.atvEnabled ? 'aan' : 'uit'}`)
  }
  if ((oud.startDate?.toISOString() ?? null) !== (nieuw.startDate?.toISOString() ?? null)) {
    parts.push(`startdatum gewijzigd`)
  }
  if ((oud.endDate?.toISOString() ?? null) !== (nieuw.endDate?.toISOString() ?? null)) {
    parts.push(`einddatum gewijzigd`)
  }
  const reason = parts.length > 0 ? parts.join(', ') : 'profielwijziging'
  return `${type}: ${reason}`
}
